export interface User {
  id: string;
  email: string;
  role: 'loan_originator' | 'admin';
  name: string;
}

export interface LoanFormData {
  // Sponsor / Borrower
  sponsorName?: string;
  entityName?: string;
  sponsorApprovalStatus?: string;
  entityApprovalStatus?: string;

  // Property / Collateral
  streetAddress?: string;
  city?: string;
  state?: string;
  zip?: string;
  msa?: string;
  existingPropertyType?: string;
  finishedPropertyType?: string;
  units?: number;
  estimatedAIV?: number;
  estimatedARV?: number;

  // Loan Details
  otcLoan?: boolean;
  loanProgram?: string;
  productType?: string;
  rehabStatus?: string;
  exitStrategy?: string;
  estimatedFundingDate?: string;
  loanAmount?: number;
  purchasePrice?: number;
  constructionHoldback?: number;
  totalRehabBudget?: number;
  creditHoldback?: number;
  termMonths?: number;
  rateType?: string;
  interestRate?: number;  // display/form as percent (9.5), stored in DB as decimal (0.095)
  originationFeePercent?: number;
  payoffPoints?: number;
  standardExtensions?: boolean;

  // Fees
  brokerFeePercent?: number;
  constructionFeeType?: string;
  costPerDraw?: number;
  drawFeePaymentType?: string;
  uwFee?: number;
  appraisalFee?: number;
  escrowTitleFees?: number;
  feeCredit?: number;
  payoffLiens?: number;
  oddDaysCount?: number;

  // Borrower Profile
  avgFICO?: number;
  yearsExperience?: number;

  // System
  applicationName?: string;
  status?: string;
  notes?: string;
}

export interface Exception {
  exceptionCode: string;
  exceptionMessage: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'INFO';
  action: string;
}

export interface RulesResult {
  borrowerTier?: string;
  maxLTC?: number | null;
  maxLTAIV?: number | null;
  maxLTARV?: number | null;
  minRate?: number | null;
  minOrigFee?: number | null;
  grossYield?: number | null;
  standardTerm?: number;
  maxTerm?: number;
  minTerm?: number;
  loanAtFunding?: number;
  constructionHoldback?: number;
  interestReserve?: number;
  originationFee?: number;
  uwFeeAmt?: number;
  drawFeeAmt?: number;
  totalLoanFees?: number;
  totalProjectCost?: number;
  ltc?: number;
  ltaiv?: number | null;
  ltarv?: number | null;
  perDiem?: number;
  initialPaymentAmount?: number;
  maxPaymentAmount?: number;
  oddDaysInterest?: number;
  cashToClose?: number;
  priceAdjustment?: number;
  adjustedMinRate?: number;
  ltcBreached?: boolean;
  ltaivBreached?: boolean;
  ltarvBreached?: boolean;
  rateBelowMin?: boolean;
  origFeeBelowMin?: boolean;
  termExceedsMax?: boolean;
  termBelowMin?: boolean;
  loanExceedsMax?: boolean;
  maxAllowedLoan?: number | null;
  irRequired?: boolean;
  exceptions?: Exception[];
  conditions?: string[];
}

export interface Loan {
  id: string;
  loanNumber?: string;
  sponsorName?: string;
  entityName?: string;
  sponsorApprovalStatus?: string;
  entityApprovalStatus?: string;
  streetAddress?: string;
  city?: string;
  state?: string;
  zip?: string;
  msa?: string;
  existingPropertyType?: string;
  finishedPropertyType?: string;
  units?: number;
  estimatedAIV?: number;
  estimatedARV?: number;
  otcLoan?: boolean;
  loanProgram?: string;
  productType?: string;
  rehabStatus?: string;
  exitStrategy?: string;
  estimatedFundingDate?: string;
  loanAmount?: number;
  purchasePrice?: number;
  constructionHoldback?: number;
  totalRehabBudget?: number;
  termMonths?: number;
  rateType?: string;
  interestRate?: number;
  originationFeePercent?: number;
  brokerFeePercent?: number;
  constructionFeeType?: string;
  costPerDraw?: number;
  uwFee?: number;
  appraisalFee?: number;
  escrowTitleFees?: number;
  feeCredit?: number;
  payoffLiens?: number;
  avgFICO?: number;
  yearsExperience?: number;
  applicationName?: string;
  status: string;
  channel?: string;
  assignedTo?: string;
  notes?: string;
  rulesResult?: RulesResult;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  user?: { name: string; email: string };
}

export interface Rule {
  id: string;
  name: string;
  description?: string;
  jdmContent: object;
  version: number;
  isActive: boolean;
  updatedBy?: string;
  updatedAt: string;
}

export const LOAN_STATUSES = [
  '01-Loan Modeling',
  '02-Initial Underwriting',
  '03-Underwriting',
  '04-Approval',
  '05-Closing',
  '06-Boarded',
  '07-Servicing',
  'declined'
] as const;
